import { useCallback, useEffect, useRef, useState } from 'react';
import { getWaveformData } from '@/common/utils/audioPlayerUtils';
import type { WaveFormInput } from '@/generated/graphql';

function useObjectUrl(file?: File) {
  const [resource, setResource] = useState<{ file: File; url: string }>();
  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    // Blob URLs are browser resources: allocate after commit and release on cleanup.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setResource({ file, url });
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return resource?.file === file ? resource?.url : undefined;
}

/** Local previews never upload assets. Reuse the same peaks when publishing. */
export function useLocalBlueprintMedia(artwork?: File, audio?: File) {
  const artworkUrl = useObjectUrl(artwork);
  const audioUrl = useObjectUrl(audio);
  const cache = useRef(new WeakMap<File, Promise<WaveFormInput | null>>());
  const [decoded, setDecoded] = useState<{
    file: File;
    waveForm: WaveFormInput | null;
  }>();
  const getWaveform = useCallback((file: File) => {
    let result = cache.current.get(file);
    if (!result) {
      result = getWaveformData(file).catch(() => null);
      cache.current.set(file, result);
    }
    return result;
  }, []);

  useEffect(() => {
    if (!audio) return;
    let current = true;
    void getWaveform(audio).then((waveForm) => {
      if (current) setDecoded({ file: audio, waveForm });
    });
    return () => {
      current = false;
    };
  }, [audio, getWaveform]);

  return {
    artworkUrl,
    audioUrl,
    waveForm: decoded?.file === audio ? decoded?.waveForm : undefined,
    decoding: !!audio && decoded?.file !== audio,
    getWaveform,
  };
}
